import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Edit2, Trash2, CheckCircle, XCircle, Newspaper, FileText } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import JoditEditor from 'jodit-react';

const API_BASE_URL = "https://backend.dlapakistan.com/api";
const API_ENDPOINTS = {
  DENTAL_NEWS: `${API_BASE_URL}/dental-news/`,
  DENTAL_NEWS_DETAIL: (id) => `${API_BASE_URL}/dental-news/${id}/`,
  TOGGLE_PUBLISH: (id) => `${API_BASE_URL}/dental-news/${id}/toggle_publish/`,
  DENTAL_NEWS_POSTS: `${API_BASE_URL}/dental-news-posts/`,
  DENTAL_NEWS_POSTS_BY_NEWS: (newsId) => `${API_BASE_URL}/dental-news-posts/by_dental_news/?dental_news_id=${newsId}`,
  DENTAL_NEWS_POST_DETAIL: (id) => `${API_BASE_URL}/dental-news-posts/${id}/`,
};

const DentalNews = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [newsItems, setNewsItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const emerald600 = "#059669";

  // Jodit Editor Configuration - Using useMemo to prevent recreation
  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Write news content here (optional)...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Fetch news items from backend - FIXED with safety checks
  const fetchNewsItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(API_ENDPOINTS.DENTAL_NEWS);
      
      // SAFETY CHECK: Ensure response.data is an array
      const newsData = Array.isArray(response.data) ? response.data : 
                      (response.data.results ? response.data.results : []);
      
      // Sort by created_at date (oldest first)
      const sortedNews = newsData.sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      
      setNewsItems(sortedNews);
    } catch (err) {
      console.error("Error fetching news items:", err);
      setError("Failed to load news items. Please try again.");
      // SAFETY CHECK: Set to empty array on error
      setNewsItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsItems();
  }, []);

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5'
      }
    });
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
  };

  const showDeleteConfirmation = (itemId, itemTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `<div class="text-left">
        <p class="mb-2">You are about to delete the news item:</p>
        <p class="font-bold text-gray-800">"${itemTitle}"</p>
        <p class="mt-3 text-sm text-gray-600">This will also delete all associated posts.</p>
        <p class="text-sm text-red-600 font-medium">This action cannot be undone.</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5',
        cancelButton: 'rounded-lg px-6 py-2.5'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        await handleDeleteConfirmed(itemId, itemTitle);
      }
    });
  };

  const handleDeleteConfirmed = async (itemId, itemTitle) => {
    try {
      // First, delete all associated posts
      try {
        const postsResponse = await axios.get(API_ENDPOINTS.DENTAL_NEWS_POSTS_BY_NEWS(itemId));
        const posts = Array.isArray(postsResponse.data) ? postsResponse.data : [];
        
        // Delete each post
        for (const post of posts) {
          await axios.delete(API_ENDPOINTS.DENTAL_NEWS_POST_DETAIL(post.id));
        }
      } catch (err) {
        console.error("Error deleting associated posts:", err);
      }
      
      // Then delete the news item
      await axios.delete(API_ENDPOINTS.DENTAL_NEWS_DETAIL(itemId));
      setNewsItems(prev => prev.filter(item => item.id !== itemId));
      
      Swal.fire({
        title: 'Deleted!',
        html: `<div class="text-left">
          <p class="mb-2">The news item has been deleted:</p>
          <p class="font-bold text-gray-800">"${itemTitle}"</p>
        </div>`,
        icon: 'success',
        confirmButtonColor: emerald600,
        confirmButtonText: 'OK',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      console.error("Error deleting item:", err);
      showErrorAlert("Failed to delete news item. Please try again.");
    }
  };

  const closeModal = () => {
    if (saving) return;
    
    setShowModal(false);
    setEditingItem(null);
    setNewTitle("");
    setNewContent("");
    setIsPublished(true);
    
    // Clear editor reference
    if (editorRef.current) {
      editorRef.current = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!newTitle.trim()) {
      showErrorAlert("Please enter a title for the news item.");
      return;
    }

    if (newTitle.trim().length < 1) {
      showErrorAlert("Title must be at least 1 character long.");
      return;
    }

    setSaving(true);

    try {
      const newsData = {
        title: newTitle.trim(),
        content: newContent.trim(),
        is_published: isPublished
      };

      if (editingItem) {
        // Update existing item
        const response = await axios.put(
          API_ENDPOINTS.DENTAL_NEWS_DETAIL(editingItem.id),
          newsData
        );
        
        setNewsItems(prev => 
          prev.map(item => 
            item.id === editingItem.id ? response.data : item
          )
        );
        
        showSuccessAlert("News item updated successfully!");
      } else {
        // Add new item
        const response = await axios.post(
          API_ENDPOINTS.DENTAL_NEWS,
          newsData
        );
        
        setNewsItems(prev => [response.data, ...prev]);
        showSuccessAlert("News item added successfully!");
      }

      closeModal();
    } catch (err) {
      console.error("Error saving item:", err);
      
      if (err.response && err.response.data) {
        if (err.response.data.title) {
          showErrorAlert(Array.isArray(err.response.data.title) 
            ? err.response.data.title.join(' ') 
            : err.response.data.title);
        } else if (err.response.data.error) {
          showErrorAlert(err.response.data.error);
        } else if (err.response.data.non_field_errors) {
          showErrorAlert(err.response.data.non_field_errors.join(' '));
        } else {
          showErrorAlert("Failed to save news item. Please try again.");
        }
      } else {
        showErrorAlert("Failed to save news item. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item, e) => {
    if (e) e.stopPropagation();
    setEditingItem(item);
    setNewTitle(item.title || "");
    setNewContent(item.content || "");
    setIsPublished(item.is_published);
    setShowModal(true);
  };

  const handleDelete = (itemId, e) => {
    if (e) e.stopPropagation();
    const item = newsItems.find(item => item.id === itemId);
    if (item) {
      showDeleteConfirmation(itemId, item.title);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setNewTitle("");
    setNewContent("");
    setIsPublished(true);
    setShowModal(true);
  };

  const togglePublishStatus = async (itemId, currentStatus, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await axios.patch(
        API_ENDPOINTS.TOGGLE_PUBLISH(itemId)
      );
      
      setNewsItems(prev => 
        prev.map(item => 
          item.id === itemId 
            ? { ...item, is_published: response.data.is_published }
            : item
        )
      );
      
      showSuccessAlert(response.data.message);
    } catch (err) {
      console.error("Error toggling publish status:", err);
      showErrorAlert("Failed to update publish status. Please try again.");
    }
  };

  // Handle clicking on a news item title to view posts
  const handleTitleClick = (item) => {
    // Save the news item data for the posts page
    localStorage.setItem('current_dental_news', JSON.stringify({
      id: item.id,
      title: item.title
    }));
    // Navigate to the posts page with the item ID
    navigate(`/dental-news/${item.id}/posts`);
  };

  const handleViewPosts = (item, e) => {
    if (e) e.stopPropagation();
    localStorage.setItem('current_dental_news', JSON.stringify({
      id: item.id,
      title: item.title
    }));
    navigate(`/dental-news/${item.id}/posts`);
  };

  const handleEditorChange = (newContent) => {
    setNewContent(newContent);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "Unknown date";
    }
  };

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Dental News</h1>
            <p className="text-gray-600 mt-1">Manage dental news titles and announcements</p>
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleAddNew}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
          >
            <Plus className="w-5 h-5 mr-2" /> Add News Item
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !newsItems || newsItems.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl hover:border-emerald-400 transition-colors">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
              <Plus className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-gray-500 text-lg mb-2 font-medium">No news items yet.</p>
            <p className="text-gray-400 text-sm">Click "Add News Item" to create your first news item</p>
            <button
              onClick={handleAddNew}
              style={{ backgroundColor: emerald600 }}
              className="mt-6 flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all mx-auto"
            >
              <Plus className="w-5 h-5 mr-2" /> Add Your First News
            </button>
          </div>
        ) : (
          <div className="space-y-4 animate-fadeIn">
            {/* SAFETY CHECK: Only map if newsItems is an array */}
            {Array.isArray(newsItems) && newsItems.map((item, index) => (
              <div 
                key={item.id} 
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:border-emerald-100 cursor-pointer group"
                onClick={() => handleTitleClick(item)}
              >
                <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50/50 to-green-50/50 flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <button
                        onClick={(e) => togglePublishStatus(item.id, item.is_published, e)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          item.is_published 
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' 
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        } transition-colors`}
                        title={item.is_published ? "Published - Click to unpublish" : "Unpublished - Click to publish"}
                      >
                        {item.is_published ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Published
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3" />
                            Unpublished
                          </>
                        )}
                      </button>
                      <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        <FileText className="w-3 h-3" />
                        {item.posts_count || 0} Posts
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Newspaper className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <h2 className="text-xl font-bold text-gray-900 truncate pr-4 group-hover:text-emerald-600 transition-colors">
                          {item.title}
                        </h2>
                      </div>
                 
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleViewPosts(item, e)}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
                      title="View Posts"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="hidden sm:inline">Posts</span>
                    </button>
                    <button
                      onClick={(e) => handleEdit(item, e)}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                    <button
                      onClick={(e) => handleDelete(item.id, e)}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Show content preview if exists */}
                  {item.content && (
                    <div className="mb-4">
                      <div 
                        className="text-gray-700 whitespace-pre-line line-clamp-2"
                        dangerouslySetInnerHTML={{ 
                          __html: item.content.length > 150 
                            ? item.content.substring(0, 150) + '...' 
                            : item.content 
                        }} 
                      />
                    </div>
                  )}
                  
                  <div className="mt-2 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-500 gap-4">
                    <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
                      {item.created_at && (
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg">
                          <span className="font-medium text-gray-700">Created:</span> 
                          <span className="text-gray-600">{formatDate(item.created_at)}</span>
                        </div>
                      )}
                      {item.updated_at && item.updated_at !== item.created_at && (
                        <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg">
                          <span className="font-medium text-blue-700">Updated:</span> 
                          <span className="text-blue-600">{formatDate(item.updated_at)}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-emerald-600 font-medium bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-100">
                      News #{index + 1}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL - WITH FIXED TEXT EDITOR */}
      {showModal && (
        <>
          <div 
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" 
            onClick={closeModal} 
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100 max-h-[90vh] flex flex-col">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gradient-to-r from-emerald-50/30 to-gray-50/30">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingItem ? "Edit News Item" : "Add New News Item"}
                </h2>
                <button 
                  onClick={closeModal} 
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto flex-grow">
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    News Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={newTitle} 
                    onChange={(e) => setNewTitle(e.target.value)} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                    placeholder="Enter news title here..." 
                    required 
                    disabled={saving}
                    maxLength={255}
                    autoFocus
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500 ml-1">
                      Minimum 1 character
                    </p>
                    <p className={`text-xs ${newTitle.length === 255 ? 'text-red-500' : 'text-gray-500'}`}>
                      {newTitle.length}/255 characters
                    </p>
                  </div>
                </div>

                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Content (Optional)
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={newContent}
                      config={editorConfig}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== newContent) {
                          setNewContent(newContent);
                        }
                      }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500 ml-1">
                      Use the toolbar for rich text formatting
                    </p>
                    <p className={`text-xs ${newContent.length > 5000 ? 'text-red-500' : 'text-gray-500'}`}>
                      Content length: {newContent.length} characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                      disabled={saving}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-gray-700 font-medium">
                      Publish this news item
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Published items will be visible to users
                  </p>
                </div>
                
                <div className="pt-4">
                  <button 
                    type="submit" 
                    style={{ backgroundColor: emerald600 }} 
                    className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 active:brightness-95 transition-all duration-200 text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        {editingItem ? 'Updating...' : 'Creating...'}
                      </>
                    ) : editingItem ? (
                      <>
                        <Edit2 className="w-5 h-5 mr-2" />
                        Update News Item
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5 mr-2" />
                        Add News Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95) translateY(20px); } 
          to { opacity: 1; transform: scale(1) translateY(0); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
          border-radius: 16px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 16px 16px 0 0;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 20px !important;
          min-height: 200px;
          border-radius: 0 0 16px 16px;
        }
      `}</style>
    </div>
  );
};

export default DentalNews;